import React from 'react';
import { X, Plus } from 'lucide-react';
import { StyledSelect } from '../../FormStyles';
import {
  Stack,
  RuleCard,
  RuleIndex,
  FieldLabel,
  TextInput,
  SelectWrap,
  DeleteBtn,
  AddButton,
  Empty,
} from './ruleEditorStyles';

const DEFAULT_RULE_TYPES = [
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'contains', label: 'Contains' },
  { value: 'notContains', label: 'Does not contain' },
  { value: 'regex', label: 'Regex pattern' },
  { value: 'isNumeric', label: 'Is numeric' },
  { value: 'isAlphanumeric', label: 'Is alphanumeric' },
  { value: 'minLength', label: 'Min length' },
  { value: 'maxLength', label: 'Max length' },
  { value: 'exactLength', label: 'Exact length' },
];

const NO_VALUE_TYPES = new Set(['isNumeric', 'isAlphanumeric']);

const placeholderFor = (type) => {
  switch (type) {
    case 'regex': return '^[0-9]+$';
    case 'startsWith': return 'https://';
    case 'endsWith': return '.pdf';
    case 'contains': return 'example';
    case 'notContains': return 'spam';
    case 'minLength': return '10';
    case 'maxLength': return '50';
    case 'exactLength': return '13';
    default: return 'Enter value...';
  }
};

export const ValidationRuleEditor = ({
  rules = [],
  onChange,
  availableTypes = DEFAULT_RULE_TYPES,
}) => {
  const updateRule = (index, patch) => {
    const next = rules.map((rule, i) => (i === index ? { ...rule, ...patch } : { ...rule }));
    onChange(next);
  };

  const changeRuleType = (index, newType) => {
    const updated = { ...rules[index], type: newType };
    if (NO_VALUE_TYPES.has(newType)) {
      delete updated.value;
      delete updated.pattern;
    } else if (newType === 'regex') {
      delete updated.value;
      updated.pattern = updated.pattern || '';
    } else {
      delete updated.pattern;
      updated.value = updated.value || '';
    }
    const next = rules.map((rule, i) => (i === index ? updated : { ...rule }));
    onChange(next);
  };

  const removeRule = (index) => {
    onChange(rules.filter((_, i) => i !== index));
  };

  const addRule = () => {
    onChange([...rules, { type: 'contains', value: '', errorMessage: '' }]);
  };

  return (
    <Stack>
      {rules.length === 0 && (
        <Empty>No rules yet. Add one below to restrict what the scanner accepts.</Empty>
      )}

      {rules.map((rule, index) => {
        const isRegex = rule.type === 'regex';
        const needsValue = !NO_VALUE_TYPES.has(rule.type);
        return (
          <RuleCard key={index}>
            <RuleIndex>Rule {index + 1}</RuleIndex>

            <div>
              <FieldLabel>Validation Type</FieldLabel>
              <SelectWrap>
                <StyledSelect
                  value={rule.type || 'contains'}
                  onChange={(e) => changeRuleType(index, e.target.value)}
                >
                  {availableTypes.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </StyledSelect>
              </SelectWrap>
            </div>

            {needsValue && (
              <div>
                <FieldLabel>{isRegex ? 'Pattern' : 'Value'}</FieldLabel>
                <TextInput
                  value={isRegex ? (rule.pattern || '') : (rule.value || '')}
                  onChange={(e) => updateRule(index, isRegex
                    ? { pattern: e.target.value }
                    : { value: e.target.value })
                  }
                  placeholder={placeholderFor(rule.type)}
                />
              </div>
            )}

            <div>
              <FieldLabel>Error Message</FieldLabel>
              <TextInput
                value={rule.errorMessage || ''}
                onChange={(e) => updateRule(index, { errorMessage: e.target.value })}
                placeholder="Shown to the user when this rule fails"
              />
            </div>

            <DeleteBtn
              type="button"
              aria-label={`Remove rule ${index + 1}`}
              title="Remove rule"
              onClick={() => removeRule(index)}
            >
              <X size={16} />
            </DeleteBtn>
          </RuleCard>
        );
      })}

      <AddButton type="button" onClick={addRule}>
        <Plus size={14} />
        Add Validation Rule
      </AddButton>
    </Stack>
  );
};
